/**
 * The local database, which is what the UI actually reads.
 *
 * Nothing in this app renders from a network response. The user taps, the change lands here,
 * the screen redraws, and the queue catches up with the server whenever it can. A spinner
 * while someone is standing in a cold garage holding groceries is exactly the friction the
 * whole product exists to remove.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import type {
  Grocery,
  InventoryItem,
  Location,
  QueuedOperation,
  ShoppingEntry,
} from '../api/types';

const DATABASE_NAME = 'shelf-aware';

/** Bumped to 2 when the household's own grocery list arrived. */
const DATABASE_VERSION = 2;

/** A mutation the user has made that the server has not acknowledged yet. */
export interface OutboxEntry {
  /** Allocated by IndexedDB, and the order the queue drains in. */
  position?: number;
  /**
   * The idempotency key, minted once when the mutation is queued and reused for every attempt
   * afterwards. Minting it at send time instead would make every retry a fresh request.
   */
  key: string;
  operation: QueuedOperation;
  queuedAt: string;
}

export interface ShelfAwareDb extends DBSchema {
  items: { key: string; value: InventoryItem };
  locations: { key: string; value: Location };
  groceries: { key: string; value: Grocery };
  shopping: { key: string; value: ShoppingEntry };
  outbox: { key: number; value: OutboxEntry };
  meta: { key: string; value: unknown };
}

export type Database = IDBPDatabase<ShelfAwareDb>;

/**
 * Opens the local database, creating whatever it is missing.
 *
 * Each store is created only if it is not already there, so an upgrade from a version this
 * device already holds adds the new store and leaves the rest — with the outbox and its unsent
 * work intact, which is the whole reason the upgrade cannot simply start again.
 */
export function openDatabase(name = DATABASE_NAME): Promise<Database> {
  return openDB<ShelfAwareDb>(name, DATABASE_VERSION, {
    upgrade(db) {
      create(db, 'items', 'id');
      create(db, 'locations', 'id');
      create(db, 'groceries', 'slug');
      create(db, 'shopping', 'id');
      create(db, 'outbox', 'position');
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta');
      }
    },
  });
}

type Keyed = 'items' | 'locations' | 'groceries' | 'shopping' | 'outbox';

function create(db: IDBPDatabase<ShelfAwareDb>, store: Keyed, keyPath: string): void {
  if (db.objectStoreNames.contains(store)) {
    return;
  }

  db.createObjectStore(store, {
    keyPath,
    ...(store === 'outbox' ? { autoIncrement: true } : {}),
  });
}

/** Keys in the `meta` store, which holds everything that is not an entity. */
export const META = {
  /** The highest event sequence this device has seen. */
  syncedSeq: 'synced-seq',
  /**
   * Sequences this device caused itself.
   *
   * Its own changes were applied optimistically before they were sent, so replaying them from
   * the log would be pointless work — and, for anything needing a refresh, a visible stutter.
   */
  ownSeqs: 'own-seqs',
  /**
   * When this device last read the household in full, or absent if it never has.
   *
   * The log reports change, not state, so a device holding nothing cannot reach a correct copy
   * by replaying a delta — least of all a household whose locations were seeded before its
   * first event.
   */
  refreshedAt: 'refreshed-at',
} as const;

export async function readMeta<T>(db: Database, key: string, fallback: T): Promise<T> {
  const stored = await db.get('meta', key);

  return stored === undefined ? fallback : (stored as T);
}

export async function writeMeta(db: Database, key: string, value: unknown): Promise<void> {
  await db.put('meta', value, key);
}
